import { useId, useRef, useState } from "react";
import { UploadError, compressForWeb, uploadImage } from "../api/upload";
import { useSettings } from "../settings/context";
import { type SafeArea, parseSafe, writeSafe } from "../site/bandLayout";
import { SafeAreaPicker } from "./SafeAreaPicker";
import { Button } from "../ui/Button";
import { ColorField } from "./ColorField";

/**
 * IMPORTING AN IMAGE FROM THE PANEL.
 *
 * What is stored is an ADDRESS, not the file: the browser sends the bytes to S3
 * itself (see `api/upload.ts`), and the setting holds the public URL that comes
 * back. So an image behaves like every other setting — it cascades by language
 * and by typology, it is inherited, and it is cleared by removing the key.
 *
 * ⚠️ A FAILURE IS SAID OUT LOUD, in the row. An import that fails silently leaves
 * an organiser looking at the old logo, convinced the new one is on its way; the
 * first they would hear of it is a participant asking why the header is wrong.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function Upload({
  settingKey,
  label,
  safeKey,
  sidesKey,
  maxWidth,
}: {
  settingKey: string;
  label: string;
  /** Le réglage qui porte la zone à garder visible, s'il y en a un. */
  safeKey?: string;
  /** Le réglage qui porte la couleur des côtés, s'il y en a un. */
  sidesKey?: string;
  /** La largeur à laquelle l'image est réduite avant l'envoi. Absente : telle quelle. */
  maxWidth?: number;
}) {
  const { value, write, clear } = useSettings();
  const current = value(settingKey);
  const url = typeof current === "string" ? current : "";
  const area = safeKey ? parseSafe(value(safeKey)) : null;
  const put = (next: SafeArea) => {
    if (safeKey) write(safeKey, writeSafe(next));
  };

  /* La couleur imposée aux côtés, s'il y en a une : vide veut dire « celle de l'image ». */
  const sides = sidesKey && typeof value(sidesKey) === "string" ? String(value(sidesKey)) : "";

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /**
   * LA TAILLE DU FICHIER SOURCE, EN PIXELS, sous la vignette de l'atelier. L'aperçu
   * fait 72 px de haut quelle que soit l'image : rien n'y distingue un bandeau de
   * 2400 px d'un autre de 600 px, alors que c'est ce qui décide s'il sera net sur un
   * grand écran. Mesurée sur l'image chargée (`naturalWidth`), donc juste par
   * construction ; rangée avec son adresse pour ne jamais afficher les dimensions de
   * l'image précédente après un remplacement.
   */
  const [measured, setMeasured] = useState<{
    url: string;
    width: number;
    height: number;
  } | null>(null);
  const size = measured && measured.url === url ? measured : null;
  const input = useRef<HTMLInputElement>(null);
  const id = useId();

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setProblem(null);
    setBusy(true);
    try {
      /* ⚠️ RÉDUITE ET RECOMPRESSÉE AVANT L'ENVOI, quand le réglage le demande (voir
         `maxWidth`) : c'est le fichier que chaque visiteur téléchargera. Le plafond de
         `uploadImage` porte sur ce qui part — un export de 8 Mo qui fond à 400 Ko passe. */
      const body = maxWidth ? await compressForWeb(file, maxWidth) : file;
      write(settingKey, await uploadImage(body));
    } catch (error) {
      setProblem(
        error instanceof UploadError
          ? error.message
          : "L'import a échoué. Réessayez dans un instant.",
      );
    } finally {
      setBusy(false);
      // The same file picked twice in a row fires no `change` event unless the
      // control is emptied — which is exactly what someone does after a failure.
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="upl">
      {url &&
        (safeKey ? (
          <SafeAreaPicker
            url={url}
            label={label}
            area={area}
            onChange={put}
            onClear={() => clear(safeKey)}
            onBroken={() => setProblem("Cette image ne se charge plus.")}
            onMeasured={({ width, height }) =>
              setMeasured({ url, width, height })
            }
          />
        ) : (
          <img
            className="upl__prev"
            src={url}
            alt={`Aperçu : ${label}`}
            /* A broken address must not read as "no image": the organiser needs to
               know the file is gone rather than never chosen. */
            onError={() => setProblem("Cette image ne se charge plus.")}
          />
        ))}

      {url && safeKey && size && (
        <p className="upl__dims">
          Fichier source : {size.width} × {size.height} px
        </p>
      )}

      {/* ⚠️ SEULEMENT S'IL Y A UNE IMAGE, comme dans le prototype : « le point focal
          n'a de sens que sur une image », et une zone à préserver encore moins. */}
      {url && safeKey && (
        <p className="upl__hint">
          {area
            ? "Cette zone reste affichée en entier sur tous les écrans. Le reste de l'image s'affiche autour s'il y a la place."
            : "Glissez sur l'image pour délimiter ce qui doit rester visible en entier. Sans zone, l'image est simplement centrée et recadrée."}
        </p>
      )}

      {/* LA COULEUR DES CÔTÉS, quand l'image ne couvre pas toute la largeur.
          Elle se devine d'après le bord de l'image, ce qui convient presque toujours ;
          mais une affiche dont le bord n'est pas la couleur qu'on aurait choisie existe
          aussi, et l'organisateur doit pouvoir trancher. Vide = devinée. */}
      {url && sidesKey && (
        <>
          <div className="row__l">
            <span>Couleur des côtés</span>
          </div>
          <ColorField
            label="Couleur des côtés du bandeau"
            value={sides}
            fallback="#ffffff"
            placeholder="d'après l'image"
            onChange={(hex) => write(sidesKey, hex)}
          >
            {sides && (
              <Button
                type="button"
                variant="ghost"
                className="btn--off"
                onClick={() => clear(sidesKey)}
              >
                Automatique
              </Button>
            )}
          </ColorField>
        </>
      )}

      <div className="upl__acts">
        <input
          ref={input}
          id={id}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void pick(e.target.files?.[0])}
        />
        <Button
          type="button"
          variant="ghost"
          loading={busy}
          onClick={() => input.current?.click()}
        >
          {url ? "Remplacer" : "Importer une image"}
        </Button>
        {url && !busy && (
          <Button
            type="button"
            variant="ghost"
            className="btn--off"
            /* La zone part AVEC l'image : sans image à cadrer, elle ne veut plus
               rien dire, et elle reviendrait s'appliquer à la prochaine. Un
               REMPLACEMENT, lui, la garde — on remplace le plus souvent par une
               variante du même visuel. */
            onClick={() => {
              clear(settingKey);
              if (safeKey) clear(safeKey);
            }}
          >
            Retirer
          </Button>
        )}
        {/* Tracer à la souris n'est pas donné à tout le monde : « Toute l'image »
            pose une zone d'un clic, qu'on rétrécit ensuite au clavier. */}
        {url && safeKey && !area && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => put({ x: 0, y: 0, w: 1, h: 1 })}
          >
            Toute l'image
          </Button>
        )}
        {url && safeKey && area && (
          <Button
            type="button"
            variant="ghost"
            className="btn--off"
            onClick={() => clear(safeKey)}
          >
            Effacer la zone
          </Button>
        )}
      </div>

      {problem && <p className="upl__err">{problem}</p>}
    </div>
  );
}
